import { useState } from "react";
import { api, fmtEUR, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Search, Undo2, Check } from "lucide-react";

export default function PosReturns() {
  const [query, setQuery] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [qtyMap, setQtyMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    try {
      const r = await api.get(`/pos/invoice-by-number/${encodeURIComponent(query.trim().toUpperCase())}`);
      setInvoice(r.data);
      const initMap = {};
      (r.data.items || []).forEach(it => { initMap[it.product_id] = 0; });
      setQtyMap(initMap);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fatura nuk u gjet");
      setInvoice(null);
    }
    setLoading(false);
  };

  const setReturnQty = (pid, remaining, val) => {
    const v = Math.max(0, Math.min(remaining, parseInt(val) || 0));
    setQtyMap(prev => ({ ...prev, [pid]: v }));
  };

  const totalItems = Object.values(qtyMap).reduce((s, v) => s + v, 0);

  const submit = async () => {
    if (totalItems === 0) { toast.error("Zgjidh së paku 1 produkt"); return; }
    if (!window.confirm(`Kthe ${totalItems} produkte nga fatura ${invoice.invoice_number}? Stoku do të rikthehet.`)) return;
    setSubmitting(true);
    try {
      const items = Object.entries(qtyMap).filter(([, q]) => q > 0).map(([product_id, qty]) => ({ product_id, qty }));
      const r = await api.post(`/pos/invoice/${invoice.id}/return`, { items });
      toast.success(`U kthyen ${fmtEUR(r.data.returned_amount)}. Statusi: ${r.data.status === "returned" ? "E kthyer plotësisht" : "Kthim i pjesshëm"}`);
      setInvoice(null); setQuery(""); setQtyMap({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim gjatë kthimit");
    }
    setSubmitting(false);
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-4">
        <Undo2 size={26} className="text-orange-400" />
        <h1 className="font-display text-2xl sm:text-3xl uppercase">Kthime</h1>
      </div>

      <form onSubmit={search} className="hg-card p-4 mb-4">
        <label className="text-xs uppercase tracking-widest text-neutral-400 mb-1 block">Numri i faturës</label>
        <div className="flex gap-2">
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder="p.sh. FAT-000001" autoFocus
            data-testid="return-search-input"
            className="flex-1 bg-black border border-[#262626] px-3 py-3 font-mono focus:border-[#FFD600] outline-none uppercase" />
          <button type="submit" disabled={loading} data-testid="return-search-btn"
            className="hg-btn-primary px-5 uppercase text-xs font-bold flex items-center gap-2 disabled:opacity-40">
            <Search size={16} /> {loading ? "..." : "Kërko"}
          </button>
        </div>
      </form>

      {invoice && (
        <div className="hg-card p-4" data-testid="return-invoice-details">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#262626]">
            <div>
              <div className="font-mono font-bold text-lg">{invoice.invoice_number}</div>
              <div className="text-xs text-neutral-400">{new Date(invoice.created_at).toLocaleString("sq")}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-neutral-400">Totali</div>
              <div className="font-bold text-lg">{fmtEUR(invoice.total)}</div>
              <div className="text-[10px] uppercase mt-1">
                {invoice.status === "returned" ? <span className="text-red-400">E kthyer plotësisht</span>
                  : invoice.status === "partial_return" ? <span className="text-orange-400">Kthim i pjesshëm</span>
                  : <span className="text-emerald-400">Aktive</span>}
              </div>
            </div>
          </div>

          <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold mb-2">Produktet</div>
          <div className="space-y-2 mb-4">
            {invoice.items.map(it => {
              const already = it.returned_qty || 0;
              const remaining = it.qty - already;
              return (
                <div key={it.product_id} className="border border-[#262626] p-3 flex items-center gap-3" data-testid={`return-item-${it.product_id}`}>
                  <div className="w-12 h-12 bg-white flex-shrink-0 overflow-hidden">
                    {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-contain" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate text-sm">{it.name}</div>
                    <div className="text-xs text-neutral-500 font-mono">{it.article_number || it.sku}</div>
                    <div className="text-xs text-neutral-400 mt-0.5">
                      U shitën: <span className="text-neutral-200">{it.qty}</span>
                      {already > 0 && <> • U kthyen më parë: <span className="text-orange-400">{already}</span></>}
                      {" "}• Mund të kthehen edhe: <span className="text-emerald-400">{remaining}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input type="number" min="0" max={remaining} value={qtyMap[it.product_id] || 0}
                      onChange={e => setReturnQty(it.product_id, remaining, e.target.value)}
                      disabled={remaining === 0}
                      data-testid={`return-qty-${it.product_id}`}
                      className="w-16 h-11 text-center bg-black border-2 border-[#262626] focus:border-orange-400 outline-none disabled:opacity-40" />
                    <span className="text-xs text-neutral-500 hidden sm:inline">/ {remaining}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <button onClick={submit} disabled={totalItems === 0 || submitting || invoice.status === "returned"}
            data-testid="return-submit"
            className="w-full py-3.5 bg-orange-500 hover:bg-orange-400 disabled:opacity-30 text-black font-bold uppercase text-sm flex items-center justify-center gap-2">
            <Check size={16} /> {submitting ? "..." : `Konfirmo Kthimin (${totalItems} produkte)`}
          </button>
        </div>
      )}
    </div>
  );
}
